import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Bar,
  BarChart,
  Cell,
} from 'recharts';
import { UserProgress } from '../../types/workout';
import { WorkoutUtils } from '../../utils/workoutUtils';

// Import exercises data to get RPE information
import exercisesData from '../../exercises.json';

interface MostImprovedChartsProps {
  userProgress: UserProgress | null;
}

type ChartType = 'progression' | 'rpe-accuracy' | 'strength-gains' | 'volume-progression';

const CHART_COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1',
  '#d084d0', '#ffb347', '#87ceeb', '#dda0dd', '#98fb98'
];

export const MostImprovedCharts: React.FC<MostImprovedChartsProps> = ({ userProgress }) => {
  const [selectedChart, setSelectedChart] = useState<ChartType>('progression');
  const [selectedExercise, setSelectedExercise] = useState<string>('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get all available exercises
  const availableExercises = React.useMemo(() => {
    if (!userProgress || userProgress.workoutHistory.length === 0) return [];
    
    const exerciseSet = new Set<string>();
    userProgress.workoutHistory
      .filter(session => session.completed)
      .forEach(session => {
        session.exercises.forEach(exercise => {
          exerciseSet.add(exercise.exerciseName);
        });
      });
    
    return Array.from(exerciseSet).sort();
  }, [userProgress]);

  // Get the most improved exercise as default
  const mostImproved = WorkoutUtils.getMostImprovedExercise(userProgress);

  // Set default exercise when data loads
  useEffect(() => {
    if (!selectedExercise && mostImproved.hasData) {
      setSelectedExercise(mostImproved.exerciseName);
    } else if (!selectedExercise && availableExercises.length > 0) {
      setSelectedExercise(availableExercises[0]);
    }
  }, [mostImproved, availableExercises, selectedExercise]);

  const handleChartChange = (event: SelectChangeEvent) => {
    setSelectedChart(event.target.value as ChartType);
  };

  const handleExerciseChange = (event: SelectChangeEvent) => {
    setSelectedExercise(event.target.value);
  };

  const chartOptions = [
    { value: 'progression', label: 'Weight Progression', description: 'Track weight increases over time' },
    { value: 'volume-progression', label: 'Volume Progression', description: 'Track volume increases over time' },
    { value: 'rpe-accuracy', label: 'RPE Accuracy', description: 'How accurately you estimate effort levels' },
    { value: 'strength-gains', label: 'Strength Gains', description: 'Week-to-week strength improvements' },
  ];

  const selectedOption = chartOptions.find(option => option.value === selectedChart);

  // Get exercise improvement data for the selected exercise
  const getExerciseImprovementData = (exerciseName: string) => {
    if (!userProgress || !exerciseName) return null;
    
    const exerciseProgress: { first: number; last: number; sessions: number } = {
      first: 0,
      last: 0,
      sessions: 0
    };
    
    userProgress.workoutHistory
      .filter(session => session.completed)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .forEach(session => {
        session.exercises.forEach(exercise => {
          if (exercise.exerciseName === exerciseName) {
            const maxWeight = Math.max(...exercise.sets.map(set => set.weight));
            
            if (exerciseProgress.sessions === 0) {
              exerciseProgress.first = maxWeight;
              exerciseProgress.last = maxWeight;
              exerciseProgress.sessions = 1;
            } else {
              exerciseProgress.last = maxWeight;
              exerciseProgress.sessions++;
            }
          }
        });
      });
    
    if (exerciseProgress.sessions >= 2 && exerciseProgress.first > 0) {
      const improvement = ((exerciseProgress.last - exerciseProgress.first) / exerciseProgress.first) * 100;
      return {
        exerciseName,
        improvement: Math.round(improvement * 10) / 10,
        startWeight: exerciseProgress.first,
        currentWeight: exerciseProgress.last,
        hasData: true
      };
    }
    
    return {
      exerciseName,
      improvement: 0,
      startWeight: exerciseProgress.first,
      currentWeight: exerciseProgress.last,
      hasData: exerciseProgress.sessions > 0
    };
  };

  // Get RPE data with proper exercise definitions
  const getRPEDataForExercise = (exerciseName: string) => {
    if (!userProgress || !exerciseName) return [];

    const rpeData: any[] = [];
    
    // Find the exercise definition across all weeks and days
    let exerciseDefinition: any = null;
    
    // Search through all weeks and days to find the exercise
    Object.values(exercisesData).forEach((weekData: any) => {
      Object.values(weekData).forEach((dayExercises: any) => {
        if (Array.isArray(dayExercises)) {
          const found = dayExercises.find((ex: any) => ex.exercise === exerciseName);
          if (found) {
            exerciseDefinition = found;
          }
        }
      });
    });
    
    if (!exerciseDefinition) return [];

    userProgress.workoutHistory
      .filter(session => session.completed)
      .forEach(session => {
        session.exercises.forEach(exercise => {
          if (exercise.exerciseName === exerciseName) {
            const workingSets = exercise.sets.filter(set => 
              set.setType === 'working' && 
              set.completed && 
              set.actualRpe
            );

            workingSets.forEach((set, index) => {
              const isLastSet = index === workingSets.length - 1;
              const expectedRPEString = isLastSet 
                ? exerciseDefinition.last_set_rpe 
                : exerciseDefinition.early_set_rpe;

              // Skip if expected RPE is "N/A"
              if (expectedRPEString === "N/A") return;

              const expectedRPE = WorkoutUtils.rpeToNumber(expectedRPEString);
              const actualRPE = WorkoutUtils.rpeToNumber(set.actualRpe!);
              
              if (expectedRPE > 0 && actualRPE > 0) {
                rpeData.push({
                  week: session.week,
                  expectedRPE,
                  actualRPE,
                  difference: actualRPE - expectedRPE,
                  accuracy: Math.max(0, 100 - Math.abs(actualRPE - expectedRPE) * 10)
                });
              }
            });
          }
        });
      });

    return rpeData;
  };

  const currentExerciseData = getExerciseImprovementData(selectedExercise);

  const renderChart = () => {
    if (!userProgress || userProgress.workoutHistory.length === 0 || !selectedExercise) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No exercise data available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete more workouts to see exercise progression charts
          </Typography>
        </Box>
      );
    }

    switch (selectedChart) {
      case 'progression':
        const progressionData = WorkoutUtils.getExerciseProgressionData(userProgress, selectedExercise);
        const exerciseData = progressionData.find(ex => ex.exerciseName === selectedExercise);
        
        if (!exerciseData || exerciseData.progressionData.length === 0) {
          return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No progression data for {selectedExercise}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This exercise needs more workout sessions to show progression
              </Typography>
            </Box>
          );
        }

        return (
          <Box sx={{ 
            width: '100%', 
            height: isMobile ? 300 : 400,
            '& .recharts-wrapper': {
              pointerEvents: 'none !important'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={exerciseData.progressionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week" 
                  label={{ value: 'Week', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  label={{ value: 'Weight (lbs)', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    `${value} lbs`,
                    name === 'maxWeight' ? 'Max Weight' : name
                  ]}
                  labelFormatter={(week) => `Week ${week}`}
                />
                <Line 
                  type="monotone" 
                  dataKey="maxWeight" 
                  stroke="#8884d8" 
                  strokeWidth={3}
                  dot={{ fill: '#8884d8', strokeWidth: 2, r: isMobile ? 4 : 6 }}
                  name="maxWeight"
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        );

      case 'volume-progression':
        const volumeProgressionData = WorkoutUtils.getExerciseProgressionData(userProgress, selectedExercise);
        const volumeExerciseData = volumeProgressionData.find(ex => ex.exerciseName === selectedExercise);
        
        if (!volumeExerciseData || volumeExerciseData.progressionData.length === 0) {
          return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No volume progression data for {selectedExercise}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This exercise needs more workout sessions to show volume progression
              </Typography>
            </Box>
          );
        }

        return (
          <Box sx={{ 
            width: '100%', 
            height: isMobile ? 300 : 400,
            '& .recharts-wrapper': {
              pointerEvents: 'none !important'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={volumeExerciseData.progressionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week" 
                  label={{ value: 'Week', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  yAxisId="volume"
                  label={{ value: 'Volume (lbs)', angle: -90, position: 'insideLeft' }}
                  tickFormatter={(value) => value.toLocaleString()}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  yAxisId="weight"
                  orientation="right"
                  label={{ value: 'Weight (lbs)', angle: 90, position: 'insideRight' }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    name === 'totalVolume' ? `${value.toLocaleString()} lbs` : `${value} lbs`,
                    name === 'totalVolume' ? 'Total Volume' : 'Max Weight'
                  ]}
                  labelFormatter={(week) => `Week ${week}`}
                />
                <Bar 
                  yAxisId="volume"
                  dataKey="totalVolume" 
                  fill="#82ca9d"
                  name="totalVolume"
                  opacity={0.7}
                />
                <Line 
                  yAxisId="weight"
                  type="monotone" 
                  dataKey="maxWeight" 
                  stroke="#8884d8" 
                  strokeWidth={3}
                  dot={{ fill: '#8884d8', strokeWidth: 2, r: isMobile ? 4 : 6 }}
                  name="maxWeight"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        );

      case 'rpe-accuracy':
        const rpeData = getRPEDataForExercise(selectedExercise);

        if (rpeData.length === 0) {
          return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No RPE data available for {selectedExercise}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                RPE accuracy requires both expected and actual RPE values for working sets
              </Typography>
            </Box>
          );
        }

        // Group RPE data by week and calculate average accuracy
        const weeklyRPEAccuracy = rpeData.reduce((acc, data) => {
          if (!acc[data.week]) {
            acc[data.week] = { week: data.week, totalAccuracy: 0, count: 0, accuracySum: 0 };
          }
          acc[data.week].accuracySum += data.accuracy;
          acc[data.week].count += 1;
          return acc;
        }, {} as any);

        const rpeAccuracyData = Object.values(weeklyRPEAccuracy).map((week: any) => ({
          week: week.week,
          accuracy: Math.round(week.accuracySum / week.count),
          status: week.accuracySum / week.count >= 80 ? 'Excellent' : 
                  week.accuracySum / week.count >= 60 ? 'Good' : 
                  week.accuracySum / week.count >= 40 ? 'Fair' : 'Needs Work'
        })).sort((a, b) => a.week - b.week);

        return (
          <Box sx={{ 
            width: '100%', 
            height: isMobile ? 300 : 400,
            '& .recharts-wrapper': {
              pointerEvents: 'none !important'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rpeAccuracyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week" 
                  label={{ value: 'Week', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  domain={[0, 100]}
                  label={{ value: 'Accuracy %', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip 
                  formatter={(value: number, name: string, props: any) => [
                    `${value}%`,
                    'RPE Accuracy'
                  ]}
                  labelFormatter={(week, payload) => {
                    if (payload && payload[0]) {
                      return `Week ${week} - ${payload[0].payload.status}`;
                    }
                    return `Week ${week}`;
                  }}
                />
                <Bar dataKey="accuracy" name="accuracy" radius={[4, 4, 0, 0]}>
                  {rpeAccuracyData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={
                        entry.accuracy >= 80 ? '#4caf50' : // Green for excellent
                        entry.accuracy >= 60 ? '#8bc34a' : // Light green for good  
                        entry.accuracy >= 40 ? '#ffc107' : // Yellow for fair
                        '#f44336' // Red for needs work
                      } 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        );

      case 'strength-gains':
        const strengthData = WorkoutUtils.getExerciseProgressionData(userProgress, selectedExercise);
        const strengthExerciseData = strengthData.find(ex => ex.exerciseName === selectedExercise);
        
        if (!strengthExerciseData || strengthExerciseData.progressionData.length < 2) {
          return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No strength gains data for {selectedExercise}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This exercise needs at least 2 workout sessions to show strength gains
              </Typography>
            </Box>
          );
        }

        // Calculate week-to-week gains
        const gainsData = strengthExerciseData.progressionData.map((current, index) => {
          if (index === 0) return { ...current, weightGain: 0 };
          const previous = strengthExerciseData.progressionData[index - 1];
          return {
            ...current,
            weightGain: current.maxWeight - previous.maxWeight
          };
        }).slice(1); // Remove first entry since it has no gain

        return (
          <Box sx={{ 
            width: '100%', 
            height: isMobile ? 300 : 400,
            '& .recharts-wrapper': {
              pointerEvents: 'none !important'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={gainsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week" 
                  label={{ value: 'Week', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  yAxisId="gain"
                  label={{ value: 'Weight Gain (lbs)', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  yAxisId="total"
                  orientation="right"
                  label={{ value: 'Total Weight (lbs)', angle: 90, position: 'insideRight' }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    `${value > 0 ? '+' : ''}${value} lbs`,
                    name === 'weightGain' ? 'Weight Gain' : 'Total Weight'
                  ]}
                  labelFormatter={(week) => `Week ${week}`}
                />
                <Bar 
                  yAxisId="gain"
                  dataKey="weightGain" 
                  fill="#82ca9d"
                  name="weightGain"
                />
                <Line 
                  yAxisId="total"
                  type="monotone" 
                  dataKey="maxWeight" 
                  stroke="#8884d8" 
                  strokeWidth={2}
                  dot={{ fill: '#8884d8', strokeWidth: 2, r: isMobile ? 3 : 4 }}
                  name="maxWeight"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', md: 'flex-start' }, 
          mb: 3,
          gap: { xs: 2, md: 0 }
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
              Exercise Analysis: {selectedExercise}
            </Typography>
            {selectedOption && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {selectedOption.description}
              </Typography>
            )}
            {currentExerciseData?.hasData && (
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1,
                mt: 1
              }}>
                {currentExerciseData.improvement > 0 && (
                  <Chip
                    label={`+${currentExerciseData.improvement}% improvement`}
                    size="small"
                    color="success"
                  />
                )}
                <Chip
                  label={`${currentExerciseData.startWeight}→${currentExerciseData.currentWeight} lbs`}
                  size="small"
                  variant="outlined"
                />
                {selectedExercise === mostImproved.exerciseName && mostImproved.hasData && (
                  <Chip
                    label="Most Improved"
                    size="small"
                    color="primary"
                  />
                )}
              </Box>
            )}
          </Box>
          
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2}
            sx={{ 
              width: { xs: '100%', md: 'auto' },
              minWidth: { md: 400 }
            }}
          >
            {/* Exercise Selection */}
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <Select
                value={selectedExercise}
                onChange={handleExerciseChange}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      zIndex: 1400,
                    },
                  },
                }}
                sx={{
                  '& .MuiSelect-select': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
              >
                {availableExercises.map((exercise) => (
                  <MenuItem key={exercise} value={exercise}>
                    <Box sx={{ minWidth: 0, width: '100%' }}>
                      <Typography 
                        variant="body2" 
                        fontWeight="medium"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {exercise}
                      </Typography>
                      {exercise === mostImproved.exerciseName && mostImproved.hasData && (
                        <Typography variant="caption" color="primary">
                          Most Improved
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Chart Type Selection */}
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <Select
                value={selectedChart}
                onChange={handleChartChange}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      zIndex: 1400,
                    },
                  },
                }}
              >
                {chartOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {option.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {renderChart()}
      </CardContent>
    </Card>
  );
}; 